'use client'

import { clsx } from 'clsx'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { TotalPriceBreakdown } from '@dfds-pax/shared-components'

import { isFootPassenger } from '@/app/lib/vehicleHelpers'
import AdministrationFee from '@/components/summary/AdministrationFee'
import SummaryLeg from '@/components/summary/Leg'
import { OfferInfo } from '@/components/summary/OfferInfo'
import PriceItem from '@/components/summary/PriceItem'
import SurchargeRow from '@/components/summary/Surcharge'

import { toAddonResource } from '@/utils/addonHelpers'

import { useVehicleDimensions } from '@/hooks/useVehicleDimensions'

import { NavigateButton } from '../NavigationButton'
import { Step } from '../steps'
import { BillingInformation } from './steps/BillingInformation'
import Passengers from './steps/Passengers'
import { PaymentInformation } from './steps/PaymentInformation'
import TravelInfo from './steps/TravelInfo'
import { VehicleInformation } from './steps/VehicleInformation'
import { isStepInactive, ToPassengerCategory } from './SummaryUtils'

export const SummaryContent: React.FC<{
  basket: BffInterfacesBasket.Basket
  currency: string
  currentIndex: number
  hideOffer: (departures: BffInterfacesBasket.Departure[]) => boolean
  offer: BffInterfacesBasket.Offer | undefined
  steps: Step[]
  t: any
  toggleSummary?: () => void
  totalOfferPrice: number
  totalPrice: number
}> = ({ basket, currentIndex, hideOffer, offer, steps, t, toggleSummary }) => {
  const isCurrentStepAfterExtras = currentIndex >= steps.findIndex((s) => s.stepType === 'EXTRAS') + 1
  const isCurrentStepAfterPets = currentIndex >= steps.findIndex((s) => s.stepType === 'PETS') + 1

  const vehicleDimensions = useVehicleDimensions(
    basket.vehicle?.vehicleTypeCode && !isFootPassenger(basket.vehicle.vehicleTypeCode) ? basket.vehicle.vehicleTypeCode : undefined
  )
  const trailerDimensions = useVehicleDimensions(basket.vehicle?.trailerTypeCode)

  return (
    <div className="gap-primitive-16 flex flex-col" data-e2e="stepper-summary">
      {offer && !offer.trackingOffer ? <OfferInfo offer={offer} /> : null}
      {steps.map((s, i) => {
        if (s.stepType === 'CONFIRMATION') return null
        const stepInactive = isStepInactive(s.stepType, currentIndex, i, basket) && currentIndex !== i

        return (
          <div
            className={clsx(
              'px-semantic-global-md relative w-full',
              stepInactive ? 'bg-semantic-global-disabled-light' : 'pb-semantic-global-lg bg-white'
            )}
            key={i}
          >
            <section className="py-semantic-global-lg border-semantic-global-border-lighter relative flex w-full flex-col border-b">
              <div className="gap-primitive-8 pb-md flex w-full flex-row items-center justify-between">
                <div
                  className={clsx(
                    'typography-heading-sm',
                    stepInactive ? 'text-semantic-global-disabled-dark' : 'text-semantic-global-text-default'
                  )}
                >
                  {t(s.resourceKey)}
                </div>

                {!stepInactive && s.stepType !== 'TRAVEL' && toggleSummary ? (
                  <NavigateButton isVertical={false} onClick={() => toggleSummary()} step={s} />
                ) : null}
              </div>
            </section>

            {!stepInactive ? (
              <div className="mt-primitive-16">
                {s.stepType === 'TRAVEL' ? (
                  <div className="w-full">
                    <TravelInfo
                      categories={ToPassengerCategory(basket.passengers)}
                      trailerCode={basket.vehicle?.trailerTypeCode}
                      trailerDimensions={trailerDimensions}
                      vehicleCode={basket.vehicle?.vehicleTypeCode}
                      vehicleDimensions={vehicleDimensions}
                    />
                  </div>
                ) : null}

                {s.stepType === 'DEPARTURES' ? (
                  <div className="gap-semantic-global-lg flex w-full flex-col justify-center">
                    <SummaryLeg
                      currencyCode={basket.priceStructure.currency}
                      key="outbound-summary"
                      type="outbound"
                      {...basket.departures[0]}
                      hideOfferPrice={hideOffer(basket.departures)}
                      offerPrice={
                        !offer?.trackingOffer && !offer?.addonOffer && basket.departures[0]?.offer
                          ? basket.departures[0]?.offer?.price
                          : undefined
                      }
                      ticketPrice={basket.departures[0]?.ticketPrice}
                    />

                    {basket.departures.length > 1 && basket.departures[1] ? (
                      <SummaryLeg
                        currencyCode={basket.priceStructure.currency}
                        key="return-summary"
                        type="return"
                        {...basket.departures[1]}
                        hideOfferPrice={hideOffer(basket.departures)}
                        offerPrice={
                          !offer?.trackingOffer && !offer?.addonOffer && basket.departures[1]?.offer
                            ? basket.departures[1]?.offer?.price
                            : undefined
                        }
                        ticketPrice={basket.departures[1]?.ticketPrice}
                      />
                    ) : null}
                    <SurchargeRow
                      currency={basket?.priceStructure?.currency}
                      homebound={basket.departures[1]}
                      outbound={basket.departures[0]}
                    />
                    <AdministrationFee currency={basket?.priceStructure?.currency} price={basket.priceStructure?.administrationFee} />
                  </div>
                ) : null}

                {s.stepType === 'PETS' ? (
                  <div className="gap-semantic-global-xs flex min-h-5 w-full flex-col justify-center">
                    {basket.departures[0]?.pets.map((pet, i) => (
                      <PriceItem
                        currency={basket.priceStructure.currency}
                        description={`${pet.quantity} x ${t(toAddonResource(pet.parentAddonCode, pet.addonCode, pet.typeCode))}`}
                        endToEndDescrAttr="bookingSummaryPetDescription"
                        endToEndPriceAttr="bookingSummaryPetPriceExpanded"
                        key={i}
                        price={
                          pet.unitPriceStructure.basePrice *
                          (pet.quantity + (basket.departures[1]?.pets.find((p) => p.addonCode === pet.addonCode)?.quantity ?? 0))
                        }
                      />
                    ))}

                    {basket.departures[0]?.pets.length === 0 ? (
                      <div className="typography-body-md-regular">{isCurrentStepAfterPets ? t('NO-PETS-SELECTED') : t('SELECT-PETS')}</div>
                    ) : null}
                  </div>
                ) : null}

                {s.stepType === 'EXTRAS' ? (
                  <div className="gap-semantic-global-xs flex min-h-5 w-full flex-col justify-center">
                    {basket.departures[0]?.addons.length > 0 ? (
                      <div className={clsx('flex flex-col justify-center')}>
                        <div className="flex">
                          <div className="typography-body-sm-medium" data-e2e="bookingSummaryRouteInfo">
                            {t('OUTBOUND')}
                          </div>
                        </div>
                      </div>
                    ) : null}

                    {basket.departures[0]?.addons.length === 0 ? (
                      <div className="typography-body-md-regular">
                        {isCurrentStepAfterExtras ? t('NO-EXTRAS-SELECTED') : t('SELECT-EXTRAS')}
                      </div>
                    ) : null}

                    {basket.departures[0]?.addons.map((addon, i) => (
                      <PriceItem
                        currency={basket.priceStructure.currency}
                        description={`${addon.quantity} x ${t(toAddonResource(addon.parentAddonCode, addon.addonCode, addon.typeCode))}`}
                        endToEndDescrAttr="bookingSummaryAddonDescription"
                        endToEndPriceAttr="bookingSummaryAddonPriceExpanded"
                        key={i}
                        offerPrice={addon.unitPriceStructure.offer ? addon.unitPriceStructure.offer?.price * addon.quantity : undefined}
                        price={addon.unitPriceStructure.basePrice * addon.quantity}
                      />
                    ))}
                    {basket.departures[1] && basket.departures[1]?.addons.length > 0 ? (
                      <div className={clsx('mt-primitive-16 gap-semantic-global-xs flex flex-col justify-center')}>
                        <div className="flex">
                          <div className="typography-body-sm-medium" data-e2e="bookingSummaryRouteInfo">
                            {t('RETURN')}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {basket.departures[1]
                      ? basket.departures[1]?.addons.map((addon, i) => (
                          <PriceItem
                            currency={basket.priceStructure.currency}
                            description={`${addon.quantity} x ${t(
                              toAddonResource(addon.parentAddonCode, addon.addonCode, addon.typeCode)
                            )}`}
                            endToEndDescrAttr="bookingSummaryAddonDescription"
                            endToEndPriceAttr="bookingSummaryAddonPriceExpanded"
                            key={i}
                            offerPrice={addon.unitPriceStructure.offer ? addon.unitPriceStructure.offer?.price * addon.quantity : undefined}
                            price={addon.unitPriceStructure.basePrice * addon.quantity}
                          />
                        ))
                      : null}
                  </div>
                ) : null}

                {s.stepType === 'PASSENGERS' ? (
                  <div className="gap-primitive-16 flex min-h-5 w-full flex-col justify-center">
                    <Passengers
                      outboundPassengerDepartureData={basket.departures[0]?.passengerDepartureData}
                      returnPassengerDepartureData={basket.departures[1]?.passengerDepartureData}
                      passengers={basket.passengers}
                      currency={basket.priceStructure.currency}
                    />
                  </div>
                ) : null}

                {s.stepType === 'CHECKOUT' ? (
                  <div className="gap-primitive-16 flex min-h-5 w-full flex-col justify-center">
                    {basket.vehicle && !isFootPassenger(basket.vehicle.vehicleTypeCode) ? (
                      <VehicleInformation vehicle={basket.vehicle} />
                    ) : null}
                    {basket?.customer ? <BillingInformation customer={basket.customer} t={t} /> : null}
                    {!basket?.customer ? (
                      <span className="typography-body-md-regular text-semantic-global-text-light">
                        {t('MISSING-CHECKOUT-INFORMATION')}
                      </span>
                    ) : null}
                    {basket.paymentMethod ? <PaymentInformation paymentMethod={basket.paymentMethod} t={t} /> : null}
                  </div>
                ) : null}

                {s.stepType === 'REVIEW' ? (
                  <div className="w-full">
                    <div className="typography-body-md-regular text-semantic-global-text-light">{t('REVIEW-BOOKING-MESSAGE')}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        )
      })}
      <div className="py-semantic-global-md">
        <TotalPriceBreakdown
          mode="sandy"
          total={basket.priceStructure.totalPrice}
          beforeBookingCompleted={true}
          originalPrice={basket.priceStructure.originalPrice ?? basket.priceStructure.totalPriceWithoutOffer}
          subsidies={basket.priceStructure.subsidyAmount}
          offer={
            basket.departures[0]?.offer
              ? {
                  displayText: basket.departures[0].offer.description,
                  hidePrice: hideOffer(basket.departures),
                  isTracking: basket.departures[0].offer.trackingOffer,
                  price: basket.departures[0].offer.price,
                }
              : undefined
          }
          currency={basket.priceStructure.currency}
          residentVehicleDiscount={Boolean(basket.commercialDiscount === 'Resident' && basket.departures.some((d) => d.id))}
          t={t}
        />
      </div>
    </div>
  )
}
